import * as Chart from 'chart.js';

declare const Zone: any;

/**
 * Chart.js watches every chart's box with hidden scroll listeners + requestAnimationFrame. Registered inside
 * Angular's zone, each of those events runs a full change detection of the app — dozens per frame while the
 * page width animates. The size watching needs no Angular, so it is registered in the root zone; the chart's
 * other events (hover / click) stay in Angular's zone as before.
 */
const platform: any = (Chart as any).platform;
if (platform && platform.addEventListener && !platform.__xaOutsideZone && typeof Zone !== 'undefined') {
  const add = platform.addEventListener, remove = platform.removeEventListener;
  platform.__xaOutsideZone = true;
  platform.addEventListener = function (chart: any, type: string, listener: any) {
    return type === 'resize' ? Zone.root.run(() => add.call(platform, chart, type, listener)) : add.apply(platform, arguments);
  };
  platform.removeEventListener = function (chart: any, type: string, listener: any) {
    return type === 'resize' ? Zone.root.run(() => remove.call(platform, chart, type, listener)) : remove.apply(platform, arguments);
  };
}

/**
 * Pauses Chart.js's responsive resizing while a layout animation runs (main menu / filter rail open-close).
 * Without it every chart on the page re-renders on every frame of the width transition, which turns a 0.5s
 * slide into a few stuttering frames. While paused the canvases are only scaled by CSS (body.xa-charts-frozen);
 * when the animation ends each chart that asked to resize is resized once.
 */
const proto: any = (Chart as any).prototype;
const pending = new Set<any>();
let frozenUntil = 0;
let timer: any = null;

if (proto && proto.resize && !proto.__xaFreezable) {
  const original = proto.resize;
  proto.__xaFreezable = true;
  proto.__xaResize = original;
  proto.resize = function (silent?: boolean) {
    // silent = Chart.js's own initial sizing of a new chart: never deferred
    if (!silent && Date.now() < frozenUntil) { pending.add(this); return this; }
    return original.apply(this, arguments);
  };
}

export function freezeChartResize(ms: number) {
  if (!proto || !proto.__xaResize) return;
  frozenUntil = Date.now() + ms;
  document.body.classList.add('xa-charts-frozen');
  clearTimeout(timer);
  timer = setTimeout(() => {
    frozenUntil = 0;
    document.body.classList.remove('xa-charts-frozen');
    pending.forEach(c => { if (c.canvas && c.ctx) proto.__xaResize.call(c); });
    pending.clear();
  }, ms);
}
